import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { Form, Radio, Input, App, Select, Empty } from "antd";
import { useFormSettings, useI18n, useUpsell } from "../../../contexts";
import { LearnMoreTag } from "../../common";
import { UpsellTag } from "../../upsell";
import { useAsyncEffect, copyToClipboard } from "@devowl-wp/react-utils";
const FormSettingsMultisiteTabContent = ()=>{
    const { message } = App.useApp();
    const { __, _i } = useI18n();
    const { isPro } = useUpsell();
    const { isTcf, multisiteEndpoints: { current, forwardTo }, fetchMultisiteEndpoints } = useFormSettings();
    const [isFetchingEndpoints, setFetchingEndpoints] = useState(false);
    useAsyncEffect(async ()=>{
        setFetchingEndpoints(true);
        try {
            await fetchMultisiteEndpoints();
        } catch (e) {
            message.error(e.responseJSON.message);
            throw e;
        } finally{
            setFetchingEndpoints(false);
        }
    }, []);
    const handleCurrentEndpointCopy = useCallback((e)=>{
        copyToClipboard(e.target.parentElement.previousElementSibling.value);
        message.success(__("Successfully copied to the clipboard!"));
        e.preventDefault();
        return false;
    }, []);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Form.Item, {
                label: __("Consent Forwarding"),
                children: [
                    /*#__PURE__*/ _jsx(Form.Item, {
                        name: "isConsentForwarding",
                        noStyle: true,
                        children: /*#__PURE__*/ _jsxs(Radio.Group, {
                            children: [
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    disabled: !isPro,
                                    value: true,
                                    children: __("Enabled")
                                }),
                                /*#__PURE__*/ _jsx(Radio.Button, {
                                    disabled: !isPro,
                                    value: false,
                                    children: __("Disabled")
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        style: {
                            margin: "5px 0px 0px"
                        },
                        children: [
                            /*#__PURE__*/ _jsx(LearnMoreTag, {
                                url: __("https://devowl.io/knowledge-base/cookie-banner-consent-forwarding-setup/")
                            }),
                            /*#__PURE__*/ _jsx(UpsellTag, {
                                title: __("One consent for all your websites?"),
                                testDrive: true,
                                feature: "consent-forwarding",
                                assetName: __("pro-modal/consent-forwarding.png"),
                                description: __("In WordPress multisites or multiple instances of WordPress running for one organization, you only need to obtain consent once. Consents are automatically forwarded, so your visitor only needs to accept services and cookies once, instead of on each site again.")
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "description",
                        children: __(`You can ask the user for consent on one site, and the consent applies to other sites in a WordPress multisite installation or other standalone WordPress installations. This is useful if you run multiple WordPress sites for one organization. For example, you have a company page, landing page, and blog, and you want to show the user as few cookie banners as possible.`)
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Form.Item, {
                noStyle: true,
                shouldUpdate: (prevValues, curValues)=>prevValues.isConsentForwarding !== curValues.isConsentForwarding,
                children: (param)=>{
                    let { getFieldValue } = param;
                    const isConsentForwarding = getFieldValue("isConsentForwarding");
                    return isPro && isConsentForwarding && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            isTcf && /*#__PURE__*/ _jsx(Form.Item, {
                                wrapperCol: {
                                    offset: 6
                                },
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "notice notice-info inline below-h2 notice-alt",
                                    style: {
                                        margin: 0
                                    },
                                    children: /*#__PURE__*/ _jsx("p", {
                                        children: __("Please note that in all sites that are provided with consent forwarding, you must use exactly the same TCF vendors with the same settings (vendor restrictions). If there are any discrepancies, the consent in the TCF standardized consent (TC string) will overwrite each other according to its specification.")
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(Form.Item, {
                                noStyle: true,
                                shouldUpdate: (prevValues, nextValues)=>JSON.stringify(prevValues.forwardTo) !== JSON.stringify(nextValues.forwardTo) || JSON.stringify(prevValues.crossDomains) !== JSON.stringify(nextValues.crossDomains),
                                children: (param)=>{
                                    let { getFieldValue } = param;
                                    return (JSON.stringify(getFieldValue("forwardTo")) + JSON.stringify(getFieldValue("crossDomains"))).indexOf("http://") > -1 && /*#__PURE__*/ _jsx(Form.Item, {
                                        wrapperCol: {
                                            offset: 6
                                        },
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: "notice notice-warning inline below-h2 notice-alt",
                                            style: {
                                                margin: 0
                                            },
                                            children: /*#__PURE__*/ _jsx("p", {
                                                children: _i(__("One of your configured endpoints is using an insecure protocol (not HTTPS). For security reasons, most modern browsers reject and block the use of cross-domain cookies on unsecured pages. {{a}}Learn more.{{/a}}"), {
                                                    a: /*#__PURE__*/ _jsx("a", {
                                                        href: __("https://www.chromestatus.com/feature/5633521622188032"),
                                                        rel: "noreferrer",
                                                        target: "_blank"
                                                    })
                                                })
                                            })
                                        })
                                    });
                                }
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("Forward to"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "forwardTo",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            mode: "multiple",
                                            notFoundContent: /*#__PURE__*/ _jsx(Empty, {
                                                description: __("No additional sites endpoints found.")
                                            }),
                                            filterOption: false,
                                            loading: isFetchingEndpoints,
                                            children: Object.keys(forwardTo).map((url)=>/*#__PURE__*/ _jsx(Select.Option, {
                                                    value: url,
                                                    children: forwardTo[url]
                                                }, url))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: __("Select all sites to which the consent of the user on the current site should be forwarded.")
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(Form.Item, {
                                label: __("External 'Forward To' endpoints"),
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "crossDomains",
                                        noStyle: true,
                                        children: /*#__PURE__*/ _jsx(Input.TextArea, {
                                            autoSize: {
                                                minRows: 3
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "description",
                                        children: __("This option is only required if you want to forward consent to another WordPress installation. Please enter one URL endpoint of the Real Cookie Banner WP REST API per line. Below you find a list of all available endpoints of this WordPress installation.")
                                    }),
                                    Object.keys(current).map((url)=>/*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                /*#__PURE__*/ _jsx("label", {
                                                    children: current[url]
                                                }),
                                                /*#__PURE__*/ _jsx(Input, {
                                                    value: url,
                                                    readOnly: true,
                                                    addonAfter: /*#__PURE__*/ _jsx("button", {
                                                        className: "button-link alignright",
                                                        onClick: handleCurrentEndpointCopy,
                                                        children: __("Copy to clipboard")
                                                    })
                                                })
                                            ]
                                        }, url))
                                ]
                            })
                        ]
                    });
                }
            })
        ]
    });
};
export { FormSettingsMultisiteTabContent };
